import { useState, useEffect, useCallback, useRef } from 'react';
import { API_BASE_URL } from '../config';
import secureStorage from '../utils/secureStorage';

// ── Module-level helpers (stable, never change) ──
const getDefaultStatusSummary = () => ({
  reportVersion: 'initial',
  reportNumber: 1,
  incidentCmdrName: '',
  incidentMgmtOrg: '',
  incidentAreaSize: '',
  percentContained: '',
  completed: '',
  incidentDefinition: '',
  complexityLevel: '',
  emergencyPhase: '',
  startDate: '',
  startTime: '',
  fromDate: '',
  fromTime: '',
  toDate: '',
  toTime: ''
});

const buildHeaders = (json = false) => {
  const token = secureStorage.getToken();
  const h = {};
  if (token) h.Authorization = `Bearer ${token}`;
  if (json) h['Content-Type'] = 'application/json';
  return h;
};

const STATUS_URL = `${API_BASE_URL}/api/status-summary`;

const apiFetch = (url, opts = {}) => fetch(url, { ...opts, headers: { ...buildHeaders(!!opts.body), ...opts.headers } });

// ── Hook ──
const useStatusSummary = (exerciseId) => {
  const [statusSummaries, setStatusSummaries] = useState([]);
  const [currentStatusSummary, setCurrentStatusSummary] = useState(getDefaultStatusSummary());
  const [editingStatusSummary, setEditingStatusSummary] = useState(null);
  const [loading, setLoading] = useState(false);
  const [lastSynced, setLastSynced] = useState(null);
  const pollingRef = useRef(null);
  const editingRef = useRef(editingStatusSummary);
  const currentRef = useRef(currentStatusSummary);

  // Keep refs in sync so callbacks don't need state deps
  editingRef.current = editingStatusSummary;
  currentRef.current = currentStatusSummary;

  const fetchStatusSummaries = useCallback(async (exId, silent = false) => {
    if (!exId) return;
    try {
      if (!silent) setLoading(true);
      const res = await apiFetch(`${STATUS_URL}?exercise_id=${exId}`);
      if (res.ok) {
        setStatusSummaries(await res.json());
        setLastSynced(new Date());
      }
    } catch (err) {
      if (!silent) console.error('Error fetching status summaries:', err);
    } finally {
      if (!silent) setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (!exerciseId) return;
    fetchStatusSummaries(exerciseId);
    pollingRef.current = setInterval(() => fetchStatusSummaries(exerciseId, true), 30000);
    return () => { if (pollingRef.current) clearInterval(pollingRef.current); };
  }, [exerciseId, fetchStatusSummaries]);

  const refreshStatusSummaries = useCallback(async () => {
    if (exerciseId) await fetchStatusSummaries(exerciseId, false);
  }, [exerciseId, fetchStatusSummaries]);

  const addStatusSummary = useCallback(async (exId, extra = {}) => {
    const current = currentRef.current;
    const editing = editingRef.current;

    if (!current.incidentCmdrName.trim()) {
      window.alert('Please enter an Incident Commander name');
      return;
    }
    try {
      if (editing) {
        const res = await apiFetch(`${STATUS_URL}/${editing.id}`, {
          method: 'PUT', body: JSON.stringify({ ...current, exercise_id: exId })
        });
        if (!res.ok) throw new Error('Update failed');
        const updated = await res.json();
        setStatusSummaries(prev => prev.map(s => s.id === editing.id ? updated : s));
        setEditingStatusSummary(null);
      } else {
        const payload = { ...current, exercise_id: exId };
        // Snapshot the active emergency phase on new entries only (edits keep original)
        if (!payload.emergencyPhase && extra.emergencyPhase) {
          payload.emergencyPhase = extra.emergencyPhase;
        }
        const res = await apiFetch(STATUS_URL, {
          method: 'POST', body: JSON.stringify(payload)
        });
        if (!res.ok) throw new Error('Save failed');
        const saved = await res.json();
        setStatusSummaries(prev => [...prev, saved]);
      }
      setCurrentStatusSummary(getDefaultStatusSummary());
    } catch (err) {
      console.error('Error saving status summary:', err);
      window.alert('Error saving status summary. Please try again.');
    }
  }, []);

  const editStatusSummary = useCallback((summary) => {
    setCurrentStatusSummary({
      reportVersion: summary.reportVersion || 'initial',
      reportNumber: summary.reportNumber || 1,
      incidentCmdrName: summary.incidentCmdrName || '',
      incidentMgmtOrg: summary.incidentMgmtOrg || '',
      incidentAreaSize: summary.incidentAreaSize || '',
      percentContained: summary.percentContained || '',
      completed: summary.completed || '',
      incidentDefinition: summary.incidentDefinition || '',
      complexityLevel: summary.complexityLevel || '',
      emergencyPhase: summary.emergencyPhase || '',
      startDate: summary.startDate || '',
      startTime: summary.startTime || '',
      fromDate: summary.fromDate || '',
      fromTime: summary.fromTime || '',
      toDate: summary.toDate || '',
      toTime: summary.toTime || ''
    });
    setEditingStatusSummary(summary);
  }, []);

  const cancelEditStatusSummary = useCallback(() => {
    setCurrentStatusSummary(getDefaultStatusSummary());
    setEditingStatusSummary(null);
  }, []);

  const deleteStatusSummary = useCallback(async (summaryId) => {
    if (window.confirm('Are you sure you want to delete this status summary?')) {
      try {
        const res = await apiFetch(`${STATUS_URL}/${summaryId}`, { method: 'DELETE' });
        if (!res.ok) throw new Error('Delete failed');
        setStatusSummaries(prev => prev.filter(s => s.id !== summaryId));
      } catch (err) {
        console.error('Error deleting status summary:', err);
        window.alert('Error deleting status summary. Please try again.');
      }
    }
  }, []);

  return {
    statusSummaries, setStatusSummaries,
    currentStatusSummary, setCurrentStatusSummary,
    editingStatusSummary, setEditingStatusSummary,
    loading, lastSynced,
    addStatusSummary, editStatusSummary, cancelEditStatusSummary,
    deleteStatusSummary, fetchStatusSummaries, refreshStatusSummaries,
    getDefaultStatusSummary
  };
};

export default useStatusSummary;
